/* eslint-disable no-debugger */
import React, { useState, useEffect, useRef } from 'react';
import { Alert, Button, Select, Cascader, message } from 'antd';
import storage from '@/utils/storage';
import intl from 'react-intl-universal';
import { InfoCircleFilled, MenuOutlined, CloseOutlined } from '@ant-design/icons';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import update from 'immutability-helper';
import { connect } from 'react-redux';
import { saveShortcutConfig } from '@/api/setting';
import { updateShortcutConfig } from '@/redux/actions/user';
import { SHOP_FUNC_LIST, filterEmptyData, handleMenuAuth, filterMenuKey } from '../QuickKey/helper';
import styles from './index.module.scss';

// 定义菜单项类型
interface MenuItem {
  id: string;
  title: string;
  type: 'store' | 'group'; // 门店或集团
  itemType: 'menu' | 'func'; // 菜单或功能
  menu?: any[];
}

// 拖拽项组件
const DraggableItem: React.FC<{
  item: MenuItem;
  index: number;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  onDelete: (id: string) => void;
  onSelect: (id: any, value: any, index: number) => void;
  options: any[];
  cascaderOptions?: any[];
  dragType: string;
  onDragEnd?: (itemId: string, newIndex: number) => void;
}> = ({
  item,
  index,
  moveItem,
  onDelete,
  onSelect,
  options,
  cascaderOptions,
  dragType,
  onDragEnd,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const dragRef = React.useRef<HTMLDivElement>(null);

  // 存储当前项的引用，以便在拖拽结束时访问
  const itemRef = React.useRef(item);
  const indexRef = React.useRef(index);

  // 每次渲染时更新引用
  React.useEffect(() => {
    itemRef.current = item;
    indexRef.current = index;
  }, [item, index]);

  // 设置拖拽
  const [{ isDragging }, drag] = useDrag({
    type: dragType,
    item: () => {
      return { index, id: item.id };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (dropResult, monitor) => {
      // 确保拖拽结束时能获取到正确的索引
      if (monitor.didDrop() && onDragEnd) {
        onDragEnd(itemRef.current.id, indexRef.current);
      }
    },
  });

  // 设置放置
  const [{ isOver }, drop] = useDrop({
    accept: dragType,
    hover(draggedItem: { index: number }, monitor) {
      if (!ref.current) return;

      const dragIndex = draggedItem.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) return;

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveItem(dragIndex, hoverIndex);
      // eslint-disable-next-line no-param-reassign
      draggedItem.index = hoverIndex;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  drop(ref);
  drag(dragRef);

  const opacity = isDragging ? 0.4 : 1;
  const backgroundColor = isOver ? '#f0f5ff' : 'transparent';
  const transform = isOver ? 'scale(1.02)' : 'scale(1)';

  // 判断是否为功能类型
  const isFunctionType = 'func' in item;

  return (
    <div
      ref={ref}
      className={styles.menuItem}
      style={{
        opacity,
        backgroundColor,
        transform,
        transition: 'all 0.2s ease',
      }}
    >
      <div className={styles.selectWrapper}>
        {isFunctionType ? (
          <Select
            value={item.func}
            onChange={(value) => onSelect(item.id, value, index)}
            style={{ width: '100%' }}
            placeholder={intl.get('app_custom_quick_entry_select_function')}
            options={cascaderOptions?.map((func) => ({ value: func.value, label: func.label }))}
          />
        ) : (
          <Cascader
            value={item.menu}
            options={filterMenuKey(options)}
            onChange={(value) => {
              if (value && value.length > 0) {
                const lastValue = value[value.length - 1];
                let label = '';
                const findLabel = (items, targetValue) => {
                  for (const menuItem of items) {
                    if (menuItem.id === targetValue || menuItem.value === targetValue) {
                      return menuItem.path;
                    }
                    if (menuItem.children) {
                      const found = findLabel(menuItem.children, targetValue);
                      if (found) return found;
                    }
                  }
                  return '';
                };

                label = findLabel(options, lastValue);
                onSelect(value, label, index);
              }
            }}
            placeholder={intl.get('app_custom_quick_entry_select_menu')}
            style={{ width: '100%' }}
            expandTrigger="hover"
            fieldNames={{ label: 'name', value: 'id' }}
            displayRender={(labels) => labels.join(' - ')}
            allowClear={false}
          />
        )}
      </div>
      <div className={styles.dragHandle} ref={dragRef}>
        <MenuOutlined className={styles.dragIcon} />
      </div>
      <div className={styles.deleteIcon} onClick={() => onDelete(item.id)}>
        <CloseOutlined />
      </div>
    </div>
  );
};

// 创建一个拖拽区域组件
const DragDropArea: React.FC<{
  items: MenuItem[];
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  onDelete: (id: string) => void;
  onSelect: (id: string, value: string, index: number) => void;
  options: { value: string; label: string }[];
  cascaderOptions?: any[];
  dragType: string;
  onDragEnd?: (itemId: string, newIndex: number) => void;
}> = ({ items, moveItem, onDelete, onSelect, options, cascaderOptions, dragType, onDragEnd }) => {
  return (
    <DndProvider backend={HTML5Backend}>
      <div className={styles.menuList}>
        {console.log('items', items)}
        {items.map((item, index) => (
          <DraggableItem
            key={item.id}
            item={item}
            index={index}
            moveItem={moveItem}
            onDelete={onDelete}
            onSelect={onSelect}
            options={options}
            cascaderOptions={cascaderOptions}
            dragType={dragType}
            onDragEnd={(itemId, newIndex) => onDragEnd && onDragEnd(itemId, newIndex)}
          />
        ))}
      </div>
    </DndProvider>
  );
};

const QuickEntry = (props) => {
  const { menus = [], companyGroup = null, shortcuts = null } = props;
  // 判断是否是集团登录
  const [isGroupLogin, setIsGroupLogin] = useState<string>('');

  // 在组件加载时从 localStorage 读取登录状态
  useEffect(() => {
    try {
      const isGroup = localStorage.getItem('isGroupLogin');
      setIsGroupLogin(isGroup || '');
    } catch (error) {
      console.error('读取登录状态失败:', error);
    }
  }, []);

  // 初始菜单项
  const hasGroupAuth = Boolean(storage.get('isGroupLogin'));
  const hasStoreAuth = Boolean(storage.get('commonCode'));
  const [storeMenus, setStoreMenus] = useState<any[]>([]);
  const [groupMenus, setGroupMenus] = useState<any[]>([]);

  const saveTimeoutRef = useRef(null);

  // 初始化加载快捷键配置
  useEffect(() => {
    if (shortcuts) {
      const { storePageEntry, groupPageEntry } = shortcuts;
      if (hasStoreAuth) {
        setStoreMenus(storePageEntry);
      }
      if (hasGroupAuth) {
        setGroupMenus(groupPageEntry);
      }
    }
  }, [shortcuts]);

  // 防抖处理保存快捷键配置
  const saveShortcuts = (data) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    let currentStoreMenuShortcuts = [...storeMenus];
    let currentGroupFuncShortcuts = [...groupMenus];

    if (isGroupLogin !== 'GROUP') {
      currentStoreMenuShortcuts = data;
    } else {
      currentGroupFuncShortcuts = data;
    }
    props.updateShortcutConfig(
      hasGroupAuth
        ? {
            ...shortcuts,
            groupPageEntry: handleMenuAuth(currentGroupFuncShortcuts, companyGroup?.menus || []),
          }
        : {
            ...shortcuts,
            storePageEntry: handleMenuAuth(currentStoreMenuShortcuts, menus || []),
          },
    ); // 更新redux中的快捷键配置

    // 使用当前操作后的最新数据
    const saveParamsData = hasGroupAuth
      ? {
          ...shortcuts,
          groupPageEntry: currentGroupFuncShortcuts,
        }
      : {
          ...shortcuts,
          storePageEntry: currentStoreMenuShortcuts,
        };
    saveTimeoutRef.current = setTimeout(() => {
      // 排除空的数据
      const requestData = filterEmptyData(saveParamsData);

      saveShortcutConfig(requestData).then(() => {});
    }, 300);
  };

  const printDraggedItemInfo = (menuType: 'store' | 'group', newIndex: number) => {
    const menuItems = menuType === 'store' ? storeMenus : groupMenus;
    // 确保索引在有效范围内
    if (newIndex >= 0 && newIndex < menuItems.length) {
      console.log('menuItems', menuItems);
      // 原来的代码有误，应该传递整个数组
      saveShortcuts(menuItems);
    } else {
      console.error('索引超出范围:', newIndex, '数组长度:', menuItems.length);
    }
  };

  // 移动菜单项
  const moveStoreItem = (dragIndex: number, hoverIndex: number) => {
    const dragItem = storeMenus[dragIndex];
    const newMenus = update(storeMenus, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, dragItem],
      ],
    });

    // 更新所有项的 sort 值
    const updatedMenus = newMenus.map((item, index) => ({
      ...item,
      sort: index,
    }));

    setStoreMenus(updatedMenus);
  };

  // 在 moveGroupItem 函数中添加调试信息确认拖拽操作正常进行
  const moveGroupItem = (dragIndex: number, hoverIndex: number) => {
    if (!groupMenus || groupMenus.length <= dragIndex) {
      console.error('无效的 dragIndex 或 groupMenus 数组为空');
      return;
    }

    const dragItem = groupMenus[dragIndex];
    console.log('拖拽项:', dragItem);

    const newMenus = update(groupMenus, {
      $splice: [
        [dragIndex, 1],
        [hoverIndex, 0, dragItem],
      ],
    });

    // 更新所有项的 sort 值
    const updatedMenus = newMenus.map((item, index) => ({
      ...item,
      sort: index,
      id: item.id,
    }));

    // 保存更新后的数据
    setGroupMenus(updatedMenus);
    // 确保数据保存到后端
    saveShortcuts(updatedMenus);
  };

  // 删除菜单项 - 直接从数组中移除
  const handleDeleteStoreMenu = (id: string) => {
    const deleteItem = storeMenus.find((item) => item.id === id);
    const newMenus = storeMenus.filter((item) => item.id !== id);
    // 重新计算 sort 值
    const updatedMenus = newMenus.map((item, index) => ({
      ...item,
      sort: index,
    }));
    if (deleteItem.menu || deleteItem.func) {
      saveShortcuts(updatedMenus);
    }
    setStoreMenus(updatedMenus);
  };

  const handleDeleteGroupMenu = (id: string) => {
    const deleteItem = groupMenus.find((item) => item.id === id);
    const newMenus = groupMenus.filter((item) => item.id !== id);
    // 重新计算 sort 值
    const updatedMenus = newMenus.map((item, index) => ({
      ...item,
      sort: index,
    }));
    if (deleteItem.menu || deleteItem.func) {
      saveShortcuts(updatedMenus);
    }
    setGroupMenus(updatedMenus);
  };
  // 选择菜单
  const handleSelectStoreMenu = (id: string, path: string, index: number) => {
    const newMenus = [...storeMenus];
    if ('menu' in newMenus[index]) {
      newMenus[index] = { ...newMenus[index], menu: id, path };
    } else {
      newMenus[index] = { ...newMenus[index], func: path };
    }
    saveShortcuts(newMenus);
    setStoreMenus(newMenus);
  };

  const handleSelectGroupMenu = (id: string, path: string, index: number) => {
    const newMenus = [...groupMenus];
    if ('menu' in newMenus[index]) {
      newMenus[index] = { ...newMenus[index], menu: id, path };
    } else {
      newMenus[index] = { ...newMenus[index], func: path };
    }
    saveShortcuts(newMenus);
    setGroupMenus(newMenus);
  };

  // 添加新菜单
  const handleAddStoreMenu = () => {
    const newSort = storeMenus.length;
    setStoreMenus([
      ...storeMenus,
      {
        id: `${Date.now()}`,
        sort: newSort,
        menu: undefined,
      },
    ]);
  };

  // 添加新功能
  const handleAddStoreFunction = () => {
    const newSort = storeMenus.length;
    setStoreMenus([
      ...storeMenus,
      {
        id: `${Date.now()}`,
        sort: newSort,
        func: undefined,
      },
    ]);
  };

  // 添加新集团菜单
  const handleAddGroupMenu = () => {
    const newSort = groupMenus.length;

    setGroupMenus([...groupMenus, { id: `${Date.now()}`, sort: newSort, menu: undefined }]);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>{intl.get('app_custom_entry_quick_entry')}</div>
      <Alert
        icon={<InfoCircleFilled />}
        message={intl.get('app_custom_quick_entry_only_current_account')}
        type="info"
        showIcon
        className={styles.alert}
      />
      {isGroupLogin !== 'GROUP' ? (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            {intl.get('app_custom_quick_entry_store_interface')}
          </div>

          <DragDropArea
            items={storeMenus}
            moveItem={moveStoreItem}
            onDelete={handleDeleteStoreMenu}
            onSelect={handleSelectStoreMenu}
            options={menus} // 使用从 props 传入的菜单数据
            cascaderOptions={SHOP_FUNC_LIST} // 使用 QuickKey 中的功能列表
            dragType="store-item"
            onDragEnd={(itemId, newIndex) => {
              printDraggedItemInfo('store', newIndex);
            }}
          />

          <div className={styles.buttonGroup}>
            <Button type="primary" className={styles.addButton} onClick={handleAddStoreMenu} ghost>
              {intl.get('app_custom_quick_entry_add_menu')}
            </Button>
            <Button
              type="primary"
              className={styles.addButton}
              onClick={handleAddStoreFunction}
              ghost
            >
              {intl.get('app_custom_quick_entry_add_function')}
            </Button>
          </div>
        </div>
      ) : (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            {intl.get('app_custom_quick_entry_group_interface')}
          </div>
          <DragDropArea
            items={groupMenus}
            moveItem={(dragIndex, hoverIndex) => {
              moveGroupItem(dragIndex, hoverIndex);
            }}
            onDelete={handleDeleteGroupMenu}
            onSelect={handleSelectGroupMenu}
            options={companyGroup?.menus || []} // 使用从 props 传入的集团菜单数据
            dragType="group-item"
            onDragEnd={(itemId, newIndex) => printDraggedItemInfo('group', newIndex)}
          />

          <div className={styles.buttonGroup}>
            <Button type="primary" className={styles.addButton} onClick={handleAddGroupMenu} ghost>
              {intl.get('app_custom_quick_entry_add_menu')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  userInfo: state.user.userInfo,
  menus: state.user.menus,
  companyGroup: state.user.companyGroup,
  shortcuts: state.user.shortcuts,
});

export default connect(mapStateToProps, { updateShortcutConfig })(QuickEntry);
